import React, {useEffect} from "react";
import {ChevronDown} from "lucide-react";
import {DropdownList} from "components/common/dropdown/DropdownList.component";
import {IDropdownListItem} from "types/gaiaComponents/dropdownButton";
import translate from "../translations/translations.wrapper.jsx";
import {ICON_SIZE} from "components/common/constants";

interface RenderTabsProps<T = any> {
    items: Array<IDropdownListItem>;
    activeTab: number | null;
    hasMore?: boolean;
    maxVisibleTabs?: number;
    translate: React.FC;
}

export const Tabs = <T, >({
                              items,
                              activeTab,
                              hasMore,
                              maxVisibleTabs,
                              translate
                          }: RenderTabsProps<T>) => {

    const [showMore, setShowMore] = React.useState(false);
    const [visibleTabsCount, setVisibleTabsCount] = React.useState<number | null>(null);
    const visibleTabs = visibleTabsCount ?? (maxVisibleTabs ?? items.length);

    const moreRef = React.useRef<HTMLLIElement>(null);
    const containerRef = React.useRef<HTMLUListElement>(null);
    const lastWidthRef = React.useRef<number | null>(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        // Fallback constants; ideally should reflect actual CSS/tab design
        const MIN_TAB_WIDTH_PX = 134; // minimum width assumed for a single tab
        const MORE_TAB_WIDTH_PX = 134; // approximate width reserved for the "More" tab

        const computeVisibleTabsFromWidth = (width: number) => {
            if (!items || items.length === 0) {
                return 0;
            }

            let availableWidth = width;

            // If we may render the "More" tab and not all items fit, reserve space for it
            const maxByProp = maxVisibleTabs ?? items.length;
            const theoreticalMaxTabs = Math.floor(availableWidth / MIN_TAB_WIDTH_PX);

            // If even theoretically we could show all items without needing more, just clamp later
            // Otherwise, reserve width for the More tab when hasMore is enabled
            if (items.length > theoreticalMaxTabs) {
                availableWidth = Math.max(0, availableWidth - MORE_TAB_WIDTH_PX);
            }

            const maxTabsByWidth = Math.max(0, Math.floor(availableWidth / MIN_TAB_WIDTH_PX));

            // Respect maxVisibleTabs as an upper bound and never exceed items.length
            const computed = Math.min(maxByProp, maxTabsByWidth, items.length);

            // Always show at least one tab when we have items
            return items.length > 0 ? Math.max(1, computed) : 0;
        };

        const updateFromWidth = (width: number) => {
            if (lastWidthRef.current === width) {
                return;
            }
            lastWidthRef.current = width;
            const nextVisible = computeVisibleTabsFromWidth(width);
            setVisibleTabsCount((prev) => (prev !== nextVisible ? nextVisible : prev));
        };

        const defaultResize = () => {
            // Fallback: window resize listener
            let resizeTimeout: number | undefined;

            const handleResize = () => {
                if (!containerRef.current) return;
                const width = containerRef.current.getBoundingClientRect().width;
                updateFromWidth(Math.round(width));
            };

            const debouncedResize = () => {
                if (resizeTimeout) {
                    window.clearTimeout(resizeTimeout);
                }
                resizeTimeout = window.setTimeout(handleResize, 150);
            };

            window.addEventListener("resize", debouncedResize);

            // Initial measurement
            handleResize();

            return () => {
                window.removeEventListener("resize", debouncedResize);
                if (resizeTimeout) {
                    window.clearTimeout(resizeTimeout);
                }
            };
        };

        const withResizeObserver = () => {
            const observer = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    const width = entry.contentRect.width;
                    updateFromWidth(Math.round(width));
                }
            });

            observer.observe(container);

            const initialRect = container.getBoundingClientRect();
            if (initialRect?.width) {
                updateFromWidth(Math.round(initialRect.width));
            }

            return () => {
                observer.disconnect();
            };
        };

        // When items or maxVisibleTabs change, recompute based on the current width
        const currentWidth = container.getBoundingClientRect().width;
        if (currentWidth) {
            const nextVisible = computeVisibleTabsFromWidth(Math.round(currentWidth));
            setVisibleTabsCount((prev) => (prev !== nextVisible ? nextVisible : prev));
        }

        if (typeof ResizeObserver !== "undefined") {
            return withResizeObserver();
        } else {
            return defaultResize();
        }
    }, [hasMore, items, maxVisibleTabs]);

    useEffect(() => {
        if (!showMore) return;

        function handleClickOutside(event: MouseEvent) {
            if (moreRef.current && !moreRef.current.contains(event.target as Node)) {
                setShowMore(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showMore]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLLIElement>, item: IDropdownListItem) => {
        if (!items || items.length === 0) return;

        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            item.onClick && item.onClick();
        }
    };

    const handleShowMoreKeyDown = (event: React.KeyboardEvent<HTMLLIElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setShowMore(!showMore);
        }
    };

    if (!items || !Array.isArray(items)) return <li></li>;
    if (items.length === 0) return <li></li>;

    return (
        <ul
            ref={containerRef}
            className="ga-tabs ga-tabs--horizontal ga-tabs--keyline mt-2 my-0 px-0 attachments-preview-modal__tabs"
            role="tablist"
            aria-label="Sample Tabs"
            data-testid="__tst_tabs">
            {items.slice(0, visibleTabs).map((item, index) => (
                <li className={`ga-tabs__tab ${(activeTab === index) ? "ga-tabs__tab--selected" : ""}`}
                    key={item.key || index} tabIndex={0} role="tab"
                    onClick={item.onClick}
                    onKeyDown={(e) => handleKeyDown(e, item)}>
                    <a className={"outline-none text-decoration-none"}
                       data-toggle="tab" aria-controls="normal"
                       aria-selected="false"
                       data-testid={`__tst_tab_${item.key}`}>
                        {item.text}
                    </a>
                </li>
            ))}

            {items.length > visibleTabs && (
                <li ref={moreRef}
                    className={`ga-tabs__tab ${(activeTab !== null && activeTab >= visibleTabs) ? "ga-tabs__tab--selected" : ""}`}
                    tabIndex={0} onKeyDown={handleShowMoreKeyDown}>
                <span className={"d-flex"} onClick={() => {
                    setShowMore(!showMore);
                }}>
                   {translate("menu.more")}
                    <ChevronDown size={ICON_SIZE.SMALL} className="ga-tabs__tab-icon"/>
                </span>
                    {showMore && (
                        <div className="h-inherit">
                            <DropdownList
                                class={"mt-2"}
                                items={items.slice(visibleTabs, items.length).map((item, index) => ({
                                    onClick: item.onClick,
                                    text: item.text,
                                    dataCy: `__tst_tab_${item.key || index + visibleTabs}`,
                                    selected: (activeTab === (visibleTabs + index)),
                                    key: item.key || (index + visibleTabs).toString()
                                })) || []} position={undefined}/>
                        </div>)
                    }
                </li>)
            }
        </ul>
    );
};

const withTranslations = translate(Tabs);
export default withTranslations;

